/**
 * ConditionsScreen
 *
 * Medical conditions that affect the fertility plan.
 * Multi-select chips with "none" and "unsure" as exclusive options.
 */

import React, { useState } from "react";
import { View, Text, Pressable, ScrollView } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { Condition } from "../types";

type ConditionOption = {
  id: Condition;
  labelKey: string;
  sex?: "AFAB" | "AMAB";
};

const CONDITIONS: ConditionOption[] = [
  { id: "pcos", labelKey: "plan_builder.conditions.pcos", sex: "AFAB" },
  {
    id: "endometriosis",
    labelKey: "plan_builder.conditions.endometriosis",
    sex: "AFAB",
  },
  {
    id: "thyroid_disease",
    labelKey: "plan_builder.conditions.thyroid_disease",
  },
  {
    id: "irregular",
    labelKey: "plan_builder.conditions.irregular",
    sex: "AFAB",
  },
  { id: "loss", labelKey: "plan_builder.conditions.loss", sex: "AFAB" },
  {
    id: "diminished_ovarian_reserve",
    labelKey: "plan_builder.conditions.low_ovarian_reserve",
    sex: "AFAB",
  },
  {
    id: "male-factor",
    labelKey: "plan_builder.conditions.male_factor",
    sex: "AMAB",
  },
  { id: "cancer", labelKey: "plan_builder.conditions.cancer" },
  { id: "other", labelKey: "plan_builder.conditions.other" },
  { id: "none_or_unsure", labelKey: "plan_builder.conditions.none_or_unsure" },
];

const EXCLUSIVE: Set<Condition> = new Set(["none_or_unsure"]);

type Props = {
  onNext: () => void;
};

export default function ConditionsScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ConditionsScreen");
  const { state, updateState } = usePlanBuilder();
  const userSex =
    state.gender === "male"
      ? "AMAB"
      : state.gender === "female"
        ? "AFAB"
        : null;
  const visibleConditions = CONDITIONS.filter(
    (c) => !c.sex || c.sex === userSex,
  );
  const [selected, setSelected] = useState<Condition[]>(state.conditions);

  const handleToggle = (condition: Condition) => {
    let next: Condition[];

    if (EXCLUSIVE.has(condition)) {
      // Exclusive option — replace everything
      next = selected.includes(condition) ? [] : [condition];
    } else {
      // Regular option — toggle, clear any exclusive
      const withoutExclusive = selected.filter((c) => !EXCLUSIVE.has(c));
      if (withoutExclusive.includes(condition)) {
        next = withoutExclusive.filter((c) => c !== condition);
      } else {
        next = [...withoutExclusive, condition];
      }
    }

    setSelected(next);
    updateState({ conditions: next });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.conditions.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.conditions.subtitle")}
      </Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {visibleConditions.map((cond) => {
          const isSelected = selected.includes(cond.id);
          return (
            <Pressable
              key={cond.id}
              onPress={() => {
                capturePress(pressLabel(t(cond.labelKey)), cond.id);
                handleToggle(cond.id);
              }}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected }}
              // Inert testability hook: react-native-web 0.21 no longer
              // forwards accessibilityState.selected to the DOM, so mirror it
              // via the aria-selected prop for deterministic E2E assertions.
              aria-selected={isSelected}
            >
              <View
                style={{
                  paddingHorizontal: 14,
                  paddingVertical: 10,
                  borderRadius: 10,
                  backgroundColor: isSelected
                    ? `${EMBER.colors.text}12`
                    : `${EMBER.colors.textFaint}10`,
                  borderWidth: 1,
                  borderColor: isSelected ? EMBER.colors.text : "transparent",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: isSelected ? "600" : "400",
                    color: isSelected
                      ? EMBER.colors.text
                      : EMBER.colors.textMid,
                  }}
                >
                  {t(cond.labelKey)}
                </Text>
              </View>
            </Pressable>
          );
        })}
      </View>

      {selected.length > 0 && (
        <View style={{ marginTop: 28 }}>
          <WarmButton title={t("plan_builder.continue")} onPress={onNext} />
        </View>
      )}
    </ScrollView>
  );
}
